var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var BwVolumeBar_1;
import { customElement, property } from 'lit/decorators.js';
import { html } from 'lit';
import { BwElement } from './base/bw-element.js';
import { factorToPosition, formatDb, positionToFactor, applyDbStep, DB_MAX_FACTOR } from './lib/gain-curve.js';
// CSS lives in packages/ui-components/src/styles/bw-volume-bar.css —
// consumers must import it (Storybook preview, web SPA, desktop renderer).
// IMPORTANT: consumer must ALSO import bw-vu-meter.css (embedded child) and
// tokens.css (--hw-* construction tokens). See packages/ui-components/README.md
// "Light DOM + CSS prefix" and "Hardware-console aesthetic" decision card.
const STEPS = 1000;
let BwVolumeBar = class BwVolumeBar extends BwElement {
    constructor() {
        super(...arguments);
        this.value = 1.0;
        this.vu = 0;
        this.size = 'md';
        this.idle = false;
        this.disabled = false;
        /** Retained for back-compat (consumers may still pass it); no visible
         *  tick is rendered — amber LED segment in embedded vu-meter is the
         *  sole unity indicator per Hardware-console direction (open Q3). */
        this.unityPosition = 0.75;
        this._pendingFactor = null;
        this._rafId = null;
        this._lastFactor = this.value;
        // Physical-fader drag state — anchor on pointerdown so the thumb moves
        // by *delta* from the press point, never jumping to a click position.
        this._dragAnchorX = null;
        this._dragAnchorPos = null;
        this._flushInput = () => {
            this._rafId = null;
            if (this._pendingFactor != null) {
                this.dispatchEvent(new CustomEvent('bw-input', {
                    detail: { value: this._pendingFactor }, bubbles: true, composed: true,
                }));
                this._pendingFactor = null;
            }
        };
        this._onSliderKeydown = (e) => {
            const stepDb = e.shiftKey ? 5 : 1;
            let next = this._lastFactor;
            switch (e.key) {
                case 'ArrowRight':
                case 'ArrowUp':
                    next = applyDbStep(this._lastFactor, +stepDb);
                    e.preventDefault();
                    break;
                case 'ArrowLeft':
                case 'ArrowDown':
                    next = applyDbStep(this._lastFactor, -stepDb);
                    e.preventDefault();
                    break;
                case 'Home':
                    next = 0;
                    e.preventDefault();
                    break;
                case 'End':
                    next = DB_MAX_FACTOR;
                    e.preventDefault();
                    break;
                case '0':
                    next = 1.0;
                    e.preventDefault();
                    break;
                default: return;
            }
            this._applyAndCommit(next);
        };
        this._onSliderDblclick = (e) => {
            e.preventDefault();
            this._applyAndCommit(1.0);
        };
        this._onMouseEnter = () => { this.classList.add('is-hovering'); };
        this._onMouseLeave = () => { this.classList.remove('is-hovering'); };
        // Manual double-click detection — pointerdown preventDefault breaks the
        // browser's native click→click→dblclick chain (see comment in firstUpdated
        // where `addEventListener('dblclick', …)` is attached to host but never
        // fires under preventDefault'd pointerdown).
        //
        // Двойной клик — это два КЛИКА, то есть нажатия без движения. Раньше
        // сравнивались два pointerdown подряд, и быстрый повторный захват после
        // настоящего перетаскивания сбрасывал фейдер в 0 дБ — на живом звуке громкий
        // скачок (B-260927-fader-quick-regrab-snaps-unity). Теперь время клика
        // запоминается на отпускании и только если рука не сдвинулась дальше
        // CLICK_SLOP_PX.
        this._lastClickAt = 0;
        this._movedSinceDown = false;
        /** Pointer-down on the host kicks off a physical-fader drag.
         *  Native input's click-to-jump is suppressed via preventDefault. */
        this._onPointerDown = (e) => {
            if (this.disabled)
                return;
            if (e.button !== undefined && e.button !== 0)
                return; // primary only
            // Manual dblclick detection (see _lastClickAt rationale above).
            if (Date.now() - this._lastClickAt < BwVolumeBar_1.DBLCLICK_MS) {
                e.preventDefault();
                this._lastClickAt = 0;
                this._applyAndCommit(1.0);
                return;
            }
            // Suppress native <input type=range> jump-to-click behaviour entirely.
            e.preventDefault();
            this._movedSinceDown = false;
            this._dragAnchorX = e.clientX;
            this._dragAnchorPos = factorToPosition(this._lastFactor);
            this.classList.add('is-dragging');
            document.addEventListener('pointermove', this._onPointerMove);
            // Конец жеста — не только pointerup. Потерянный pointerup (системный
            // диалог, смена окна, отмена указателя на тач-экране) оставлял фейдер «в
            // руке», и любое следующее движение мыши по документу меняло громкость.
            document.addEventListener('pointerup', this._onPointerUp, { once: true });
            document.addEventListener('pointercancel', this._onPointerUp, { once: true });
            window.addEventListener('blur', this._onPointerUp, { once: true });
            // Hand keyboard focus to the (invisible) native input for a11y.
            const slider = this.querySelector('input.bw-volume-bar-slider');
            slider?.focus();
        };
        this._onPointerMove = (e) => {
            if (this._dragAnchorX === null || this._dragAnchorPos === null)
                return;
            // Below the click slop the hand is still clicking, not dragging: the level
            // stays put. Otherwise a 1-px tremor on the first click of a double click
            // changes the value, the release commits it, and the commit re-renders the
            // row under the second click (B-260930-knob-dblclick-reset-broken). The
            // anchor stays at the press point, so once the drag starts the thumb
            // follows the hand exactly as before.
            if (!this._movedSinceDown) {
                if (Math.abs(e.clientX - this._dragAnchorX) < BwVolumeBar_1.CLICK_SLOP_PX)
                    return;
                this._movedSinceDown = true;
            }
            const rail = this.getBoundingClientRect();
            // Travel range = wrapper width minus shoe width (shoe centres at edges).
            const shoe = this.querySelector('.bw-volume-bar-shoe');
            const shoeW = shoe?.offsetWidth ?? 12;
            const travel = Math.max(1, rail.width - shoeW);
            const deltaPx = e.clientX - this._dragAnchorX;
            const newPos = Math.max(0, Math.min(1, this._dragAnchorPos + deltaPx / travel));
            const factor = positionToFactor(newPos);
            this._lastFactor = factor;
            this._writePos(newPos);
            this._writeSliderValue(newPos);
            const tooltip = this.querySelector('.bw-volume-bar-tooltip');
            if (tooltip)
                tooltip.textContent = formatDb(factor);
            this._pendingFactor = factor;
            if (this._rafId == null)
                this._rafId = requestAnimationFrame(this._flushInput);
        };
        this._onPointerUp = () => {
            // Концов у жеста три (pointerup / pointercancel / blur) — сработать должен
            // первый, остальные застают жест уже закрытым.
            if (this._dragAnchorX === null)
                return;
            this._removeDragListeners();
            const moved = this._movedSinceDown;
            this._lastClickAt = moved ? 0 : Date.now();
            this._dragAnchorX = null;
            this._dragAnchorPos = null;
            this.classList.remove('is-dragging');
            if (this._rafId != null) {
                cancelAnimationFrame(this._rafId);
                this._flushInput();
            }
            // A click moved nothing — nothing to commit. Committing an unchanged value
            // made the desktop rebuild the tracks pane, so the second click of a double
            // click landed on a new fader with no memory of the first one
            // (B-260930-knob-dblclick-reset-broken).
            if (!moved)
                return;
            this.dispatchEvent(new CustomEvent('bw-change', {
                detail: { value: this._lastFactor }, bubbles: true, composed: true,
            }));
        };
    }
    static { BwVolumeBar_1 = this; }
    // Discrete change (keyboard, double-click reset): live AND persist. Consumers
    // wire bw-input → engine and bw-change → persistence, so a step that only
    // emitted bw-change was saved but never heard until the next load
    // (B-260930-knob-dblclick-reset-broken). Same order as bw-knob's commit:
    // live first, then persist.
    _applyAndCommit(next) {
        this._lastFactor = next;
        this.value = next;
        this.dispatchEvent(new CustomEvent('bw-input', {
            detail: { value: next }, bubbles: true, composed: true,
        }));
        this.dispatchEvent(new CustomEvent('bw-change', {
            detail: { value: next }, bubbles: true, composed: true,
        }));
    }
    static { this.DBLCLICK_MS = 400; }
    static { this.CLICK_SLOP_PX = 3; }
    _removeDragListeners() {
        document.removeEventListener('pointermove', this._onPointerMove);
        document.removeEventListener('pointerup', this._onPointerUp);
        document.removeEventListener('pointercancel', this._onPointerUp);
        window.removeEventListener('blur', this._onPointerUp);
    }
    _writePos(pos) {
        // 0..1 → CSS var consumed by shoe transform / aria-valuenow contract
        this.style.setProperty('--bw-vb-pos', pos.toFixed(4));
    }
    _writeSliderValue(pos) {
        const slider = this.querySelector('input.bw-volume-bar-slider');
        if (!slider)
            return;
        slider.value = String(Math.round(pos * STEPS));
        slider.setAttribute('aria-valuetext', formatDb(positionToFactor(pos)));
    }
    render() {
        return html `
      <bw-vu-meter
        class="bw-volume-bar-vu"
        size=${this.size}
        decoration="segmented"
        ?idle=${this.idle}
        .value=${this.vu}
      ></bw-vu-meter>
      <input
        class="bw-volume-bar-slider"
        type="range"
        min="0"
        max="1000"
        step="1"
        ?disabled=${this.disabled}
        aria-label="Volume"
      />
      <span class="bw-volume-bar-shoe" aria-hidden="true"></span>
      <span class="bw-volume-bar-tooltip" aria-hidden="true">+0.0 dB</span>
    `;
    }
    firstUpdated() {
        const slider = this.querySelector('input.bw-volume-bar-slider');
        // Keyboard stays on the native input for a11y (focus + tab).
        slider.addEventListener('keydown', this._onSliderKeydown);
        // Dblclick on host — slider has `pointer-events: none` (physical-drag is
        // host-level), so attaching dblclick to slider never fires. DAW gesture:
        // dblclick anywhere on the fader → reset to unity.
        this.addEventListener('dblclick', this._onSliderDblclick);
        // Hover state on the host.
        this.addEventListener('mouseenter', this._onMouseEnter);
        this.addEventListener('mouseleave', this._onMouseLeave);
        // Physical-drag handler on the host (replaces native input mouse path).
        this.addEventListener('pointerdown', this._onPointerDown);
        this._lastFactor = this.value;
        this._writePos(factorToPosition(this.value));
        this._writeSliderValue(factorToPosition(this.value));
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this._removeDragListeners();
        if (this._rafId != null) {
            cancelAnimationFrame(this._rafId);
            this._rafId = null;
            this._pendingFactor = null;
        }
    }
    updated(changed) {
        if (changed.has('value')) {
            this._lastFactor = this.value;
            if (!this.classList.contains('is-dragging')) {
                const pos = factorToPosition(this.value);
                this._writePos(pos);
                this._writeSliderValue(pos);
            }
            const tooltip = this.querySelector('.bw-volume-bar-tooltip');
            if (tooltip)
                tooltip.textContent = formatDb(this.value);
        }
    }
};
__decorate([
    property({ type: Number })
], BwVolumeBar.prototype, "value", void 0);
__decorate([
    property({ type: Number })
], BwVolumeBar.prototype, "vu", void 0);
__decorate([
    property({ type: String, reflect: true })
], BwVolumeBar.prototype, "size", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BwVolumeBar.prototype, "idle", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BwVolumeBar.prototype, "disabled", void 0);
__decorate([
    property({ type: Number, attribute: 'unity-position' })
], BwVolumeBar.prototype, "unityPosition", void 0);
BwVolumeBar = BwVolumeBar_1 = __decorate([
    customElement('bw-volume-bar')
], BwVolumeBar);
export { BwVolumeBar };
//# sourceMappingURL=bw-volume-bar.js.map